import { Atom } from "@effect-atom/atom";
import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { motion } from "framer-motion";
import React from "react";
import { Button } from "~/components/ui/button.tsx";
import { submitWaitlistAtom } from "~/lib/atoms/waitlist.ts";
import { checkWaitlistStatus, markWaitlistJoined } from "~/lib/waitlist.ts";

/**
 * Form state atom
 */
const emailAtom = Atom.make("");
const isSubmittingAtom = Atom.make(false);
const isSubmittedAtom = Atom.make(false);
const errorAtom = Atom.make<string | null>(null);
const waitlistCheckedAtom = Atom.make(false);

export interface WaitlistFormProps {
  size?: "default" | "lg";
  className?: string;
  onJoined?: () => void;
}

export const WaitlistForm: React.FC<WaitlistFormProps> = ({
  className = "",
  onJoined,
  size = "default",
}) => {
  const email = useAtomValue(emailAtom);
  const setEmail = useAtomSet(emailAtom);
  const isSubmitting = useAtomValue(isSubmittingAtom);
  const setIsSubmitting = useAtomSet(isSubmittingAtom);
  const isSubmitted = useAtomValue(isSubmittedAtom);
  const setIsSubmitted = useAtomSet(isSubmittedAtom);
  const error = useAtomValue(errorAtom);
  const setError = useAtomSet(errorAtom);
  const waitlistChecked = useAtomValue(waitlistCheckedAtom);
  const setWaitlistChecked = useAtomSet(waitlistCheckedAtom);

  // Get the submitWaitlist function
  const submitWaitlist = useAtomSet(submitWaitlistAtom, { mode: "promise" });

  // Check if user is already on waitlist on first render
  if (!waitlistChecked) {
    setWaitlistChecked(true);
    const isOnWaitlist = checkWaitlistStatus();
    if (isOnWaitlist) {
      setIsSubmitted(true);
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- effect-atom promise bridge
    submitWaitlist({ email })
      .then(() => {
        markWaitlistJoined();
        setIsSubmitted(true);
        setEmail("");
        setIsSubmitting(false);
        if (onJoined) {
          onJoined();
        }
      })
      .catch((err) => {
        console.error("Failed to join waitlist:", err);
        setError("Something went wrong. Please try again.");
        setIsSubmitting(false);
      });
  };

  const inputSizeClasses = size === "lg"
    ? "h-14 px-6 text-lg"
    : "h-12 px-4 text-base";

  const buttonSizeClasses = size === "lg"
    ? "h-14 px-8 text-lg"
    : "h-12 px-6 text-base";

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`text-center ${className}`}
      >
        <p className="text-white text-lg font-medium mb-2">
          🎉 You're on the list!
        </p>
        <p className="text-zinc-400 text-sm">
          We'll notify you when FlirtMachine launches.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      onSubmit={handleSubmit}
      className={`flex flex-col sm:flex-row gap-3 w-full ${className}`}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        disabled={isSubmitting}
        className={`flex-1 rounded-full ${inputSizeClasses} bg-white/10 border border-white/20 text-white placeholder:text-zinc-400 focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
      />
      <Button
        type="submit"
        disabled={isSubmitting}
        className={`${buttonSizeClasses} bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold shadow-[0_0_15px_rgba(220,20,60,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
      >
        {isSubmitting ? "Joining..." : "Join Waitlist"}
      </Button>
      {error && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="text-red-400 text-sm mt-2 sm:col-span-2"
        >
          {error}
        </motion.p>
      )}
    </motion.form>
  );
};
